// `ChatReentryRow` (`client-beekeeper-chat` "A separate control opens an existing thread without
// sending"; sketch `promptbox-entry.html`'s "Idle, with a thread to return to" state, extended by
// `beekeeper-progress-indicator` §2's `reentry-row-states.html`).
//
// One shared row, two callers: live mode's `PromptBox` (which declared the contract) and rounds
// mode's pinned `PromptBar`, which had no re-entry affordance at all — so closing the inset overlay
// put the transcript, and the version the turn cut, permanently out of reach.
//
// It carries **busy**, **failed** and **uncertain** too, because a turn's outcome behind a closed
// overlay is otherwise invisible — Close is enabled during `submitting`/`uncertain`, so dismissal
// mid-send is a reachable, not a hypothetical, path. `busy` covers `submitting` and `running`
// alike (the caller derives it from the channel's `phase`; from out here the distinction does not
// matter, only that the colony is being worked on). `failed` and `uncertain` are the two-colour
// split settled with the user: danger for a known refusal, warning for a genuinely unknown outcome
// — never both, and never together with `busy`.
//
// **The row's opacity pulse is REMOVED** (settled decision, `beekeeper-progress-indicator` §2):
// running it alongside the bee's own flap read as two tickers at unrelated rates competing on one
// small row rather than one object working. The bee (`BusyBee`, itself WAAPI-driven and
// reduced-motion-safe) is the sole motion now, so `failed`/`uncertain` carry no motion at all — tint
// and copy only, the correct floor under `prefers-reduced-motion` too.

import type { CSSProperties } from 'react';
import { BusyBee } from './BusyBee';

export interface ChatReentryRowProps {
  /** The most recent line of the conversation, shown truncated to one line. Superseded by the
   * phase-specific copy below whenever `busy`/`failed`/`uncertain` is set. */
  lastLine: string;
  /**
   * The newest thing the agent has actually done — its latest message, or the tool it is running —
   * summarised to one line (`player-chat-and-outcome` **D-3**). Shown **in place of** the generic
   * "Beekeeper is working…" while `busy`, which is what makes minimising cost the player no
   * information: the row keeps reporting the turn instead of merely asserting one exists.
   *
   * Only `busy` yields to it. `failed`/`uncertain` state an outcome the player has to act on, and
   * a stale activity line would bury it.
   */
  activityLine?: string | null;
  /**
   * The agent has replied since the player last had the overlay open — draws the halo
   * (`player-chat-and-outcome` **D-3**, `ChatSeamSnapshot.unread`).
   *
   * A ring and an accessible name, deliberately **no motion**: the row's one moving thing is the
   * busy bee, and a second loop competing with it is the mistake `reentry-row-states.html` already
   * settled once. Orthogonal to tone — an unread reply can be sitting behind any of them.
   */
  unread?: boolean;
  /** A turn is being submitted or is running. Replaces the line with the working copy, tints the
   * row, and shows the flapping bee. Takes precedence over `failed`/`uncertain` if somehow both are
   * asserted — a caller should derive at most one from the channel's phase. */
  busy: boolean;
  /** A send was refused for a demonstrably pre-admission reason: nothing was admitted, so reopening
   * and resending is safe. Danger tint, no motion. */
  failed?: boolean;
  /** The outcome of a send is genuinely unknown (a dropped connection, an unrecognised refusal).
   * Warning tint, no motion — the two-colour split from danger, since an unknown outcome is not the
   * same claim as a known failure. */
  uncertain?: boolean;
  /**
   * Narrows `failed`'s copy for ONE specific cause (`beekeeper-progress-indicator` F3, mirroring
   * `ChatOverlay`'s own `noOutputSettle`): a refresh found this submission's turn already settled
   * with NO agent output at all — the server admitted the message but nothing came back. The
   * generic "Send failed — open to retry" implies a refusal that never reached the Beekeeper, which
   * is false here; the outcome is still `failed` (nothing holds the sandbox, resending is safe), so
   * this changes only the row's copy, never its tone or its `aria-busy`/motion behaviour. Ignored
   * unless `failed` is also set.
   */
  noOutputSettle?: boolean;
  onOpen(): void;
  style?: CSSProperties;
}

const BUSY_LINE = 'Beekeeper is working…';
const FAILED_LINE = 'Send failed — open to retry';
const NO_OUTPUT_LINE = 'No reply came back — open to retry';
const UNCERTAIN_LINE = 'Delivery unknown — open to check';

type RowTone = 'idle' | 'busy' | 'failed' | 'uncertain';

function toneFor(busy: boolean, failed: boolean, uncertain: boolean): RowTone {
  if (busy) return 'busy';
  if (failed) return 'failed';
  if (uncertain) return 'uncertain';
  return 'idle';
}

const TONE_STYLE: Record<RowTone, { background: string; borderColor: string; textColor: string; glyph: string }> = {
  idle: { background: 'var(--surface-sunken)', borderColor: 'var(--line)', textColor: 'var(--ink-2)', glyph: '🐝' },
  busy: { background: 'var(--honey-100)', borderColor: 'var(--honey-300)', textColor: 'var(--honey-700)', glyph: '' },
  // `--danger-soft` needs a darker foreground than any token names for readable contrast on it —
  // the same literal `TerminalRow`/`ToolCallRow`/`TypeSheet` already use for exactly this pairing.
  failed: { background: 'var(--danger-soft)', borderColor: 'var(--danger)', textColor: '#7E2F1D', glyph: '✕' },
  uncertain: { background: 'var(--warning-soft)', borderColor: 'var(--warning)', textColor: 'var(--ink-1)', glyph: '?' },
};

const TONE_LINE: Record<RowTone, string | null> = {
  idle: null,
  busy: BUSY_LINE,
  failed: FAILED_LINE,
  uncertain: UNCERTAIN_LINE,
};

export function ChatReentryRow({
  lastLine,
  activityLine = null,
  busy,
  failed = false,
  uncertain = false,
  noOutputSettle = false,
  unread = false,
  onOpen,
  style,
}: ChatReentryRowProps) {
  const tone = toneFor(busy, failed, uncertain);
  const { background, borderColor, textColor, glyph } = TONE_STYLE[tone];
  const toneLine =
    tone === 'failed' && noOutputSettle
      ? NO_OUTPUT_LINE
      : // `busy` is the one tone that yields to live activity — see `activityLine`'s own note.
        tone === 'busy' && activityLine !== null && activityLine.length > 0
        ? activityLine
        : TONE_LINE[tone];
  const line = toneLine ?? lastLine;
  const label = tone === 'idle' ? 'Reopen the Beekeeper conversation' : line;

  return (
    <div
      data-testid="chat-thread-reopen"
      data-unread={unread}
      role="button"
      tabIndex={0}
      aria-busy={busy}
      aria-label={unread ? `New from the Beekeeper — ${label}` : label}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-2)',
        minHeight: 'var(--hit-target)',
        boxSizing: 'border-box',
        background,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor,
        borderRadius: 'var(--r-sm)',
        padding: '8px 10px',
        cursor: 'pointer',
        // The unread halo: a soft accent ring drawn OUTSIDE the box (spread, no blur inside), so it
        // reads at a glance from across the screen without moving the row or its neighbours a
        // pixel — this bar's height is budgeted by `sandbox-layout.ts`, and a border change here
        // would reflow the world view above it.
        ...(unread ? { boxShadow: '0 0 0 3px var(--accent-soft), 0 0 0 4.5px var(--accent)' } : {}),
        ...style,
      }}
    >
      {tone === 'busy' ? (
        <BusyBee animated size={16} />
      ) : (
        <span aria-hidden="true" style={{ flex: 'none', fontSize: 13 }}>
          {glyph}
        </span>
      )}
      <span
        data-testid="chat-thread-reopen-line"
        style={{
          flex: 1,
          minWidth: 0,
          fontFamily: 'var(--font-body)',
          fontSize: 12,
          color: textColor,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {line}
      </span>
      <span aria-hidden="true" style={{ flex: 'none', fontSize: 10, color: 'var(--ink-3)' }}>
        ▸
      </span>
    </div>
  );
}
